import * as PIXI from 'pixi.js';
import { buildChildrenForSceneUsingData } from '../libs/buildChildrenForScene';
import {
    getStringMetadata,
    getAnimatableAttributes,
    templateAlphaByIndex,
    templateColorByIndex,
    templateFontsByIndex,
    templateWatermarkSourcesByIndex,
    getTemplateIndexFromAssetProperty,
    getTemplateIndex,
    getTemplateProperty,
    isTemplateReference,
    getNumberMetadata,
} from '../core/attributes';
import { getGlobalFontManager } from '../core/FontManager';
import { Template } from '../core/template/Template';
import { getResolution } from '../libs/videoData';
import { Scene } from '../core/scene/Scene';
import { getAspectRatioDisplayName, getAspectRatioDisplayRatio } from '../aspectRatioConfig';

import { PlaybackRegions } from '../core/PlaybackRegions';
import { Config } from './config';

import { getPartialSceneDesignConfig } from './designs/scenes';
import { sliceVideoData } from './sliceVideoData';
import duplicateAssetData from './duplicateAssetData';

/**
 * Video Data Class
 *
 * IMPORTANT
 * All methods & variables here are exposed externally (via the "API") so any changes require ensuring
 * that any code using VideoData (in Luminary and outside) have been updated!
 */
export class VideoData extends Config {
    // scene designs
    static getPartialSceneDesignConfig = getPartialSceneDesignConfig;

    static getGlobalFontManager = getGlobalFontManager;
    static getStringMetadata = getStringMetadata;
    static getNumberMetadata = getNumberMetadata;
    static getAnimatableAttributes = getAnimatableAttributes;

    static rgb2hex = PIXI.utils.rgb2hex;
    static hex2rgb = PIXI.utils.hex2rgb;
    static hex2string = PIXI.utils.hex2string;
    static string2hex = PIXI.utils.string2hex;

    static templateAlphaByIndex = templateAlphaByIndex;
    static templateColorByIndex = templateColorByIndex;
    static templateFontsByIndex = templateFontsByIndex;
    static templateWatermarkSourcesByIndex = templateWatermarkSourcesByIndex;

    static getTemplateIndexFromAssetProperty = getTemplateIndexFromAssetProperty;
    static getTemplateProperty = getTemplateProperty;
    static isTemplateReference = isTemplateReference;
    static getTemplateIndex = getTemplateIndex;

    // template data helpers
    static duplicateTemplateData = Template.duplicateTemplateData;
    static getColorsFromTemplateData = Template.getColorsFromTemplateData;
    static getFontsFromTemplateData = Template.getFontsFromTemplateData;
    static getWatermarkSourcesFromTemplateData = Template.getWatermarkSourcesFromTemplateData;

    // scene data helpers (lazy to avoid circular dependency TDZ)
    static duplicateSceneData(...args: Parameters<typeof Scene.duplicateSceneData>) {
        return Scene.duplicateSceneData(...args);
    }
    static getSceneCategoryFromSceneData(
        ...args: Parameters<typeof Scene.getSceneCategoryFromSceneData>
    ) {
        return Scene.getSceneCategoryFromSceneData(...args);
    }

    static sliceVideoData = sliceVideoData;
    static getResolution = getResolution;
    static buildChildrenForSceneUsingData = buildChildrenForSceneUsingData;
    static duplicateAssetData = duplicateAssetData;

    // playback regions utilities
    static getCutRegions = PlaybackRegions.getCutRegions;
    static assetTimeToPlaybackTime = PlaybackRegions.assetTimeToPlaybackTime;
    static playbackTimeToAssetTime = PlaybackRegions.playbackTimeToAssetTime;

    // aspect ratio utilities
    static getAspectRatioDisplayName = getAspectRatioDisplayName;
    static getAspectRatioDisplayRatio = getAspectRatioDisplayRatio;
}
